import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';

import ExternalFileOptions from 'external-files/ExternalFileOptions';
import ExternalFilePreview from 'external-files/ExternalFilePreview';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import FileStates from 'FileStates';
import * as navigation from 'Navigation';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';

class BlobLinkTabState extends navigation.TabState {
  blob(): BenchlingBlob {
    return this.get('blob');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaults(): any {
    return {
      ...super.defaults(),
      title: 'Preview File',
      closeable: true,
      blob: null,
      previewType: null,
    };
  }

  static open(fileId: string, blob: BenchlingBlob, owningEntityApiId: string): void {
    if (blob.previewFile == null && blob.pdfPreviewFile == null) {
      throw new Error('Attempted to preview a blob without a previewFile');
    }

    const fileState = FileStates.get().get(fileId);
    const existingTab = fileState.findTab(
      BlobLinkTabState,
      (tab: BlobLinkTabState) => tab.blob().id === blob.id
    );
    if (existingTab != null) {
      existingTab.set({active: true});
      return;
    }

    const newTab = new BlobLinkTabState({
      fileId,
      title: blob.name,
      blob,
      previewType: blob.previewFile ? blob.previewFile.previewType : blob.pdfPreviewFile?.previewType,
      owningEntityApiId,
    });
    fileState.buffers().addTab(newTab);
  }
}

class BlobLinkTabView extends Backbone.View<BlobLinkTabState> {
  initialize(): void {
    this.listenTo(this.model, 'change', () => this.render());
  }

  render(): this {
    const previewType = this.model.get('previewType');
    const owningEntityApiId = this.model.get('owningEntityApiId');
    const {previewFile, pdfPreviewFile} = this.model.blob();
    const chosenFile = previewType === 'image' ? previewFile : pdfPreviewFile;
    if (chosenFile == null) {
      throw new Error('previewFile is null');
    }
    renderToDOM(
      <ExternalFilePreview
        url={chosenFile.url}
        externalFileId={chosenFile.id}
        previewType={this.model.get('previewType')}
        owningEntityApiId={owningEntityApiId}
      />,
      this.el
    );
    return this;
  }

  remove(): Backbone.View<BlobLinkTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

class BlobLinkOptionsView extends Backbone.View<BlobLinkTabState> {
  initialize(): void {
    this.listenTo(this.model, 'change', () => this.render());
  }

  updatePreviewType = (previewType: string): void => {
    this.model.set('previewType', previewType);
  };

  render = (): this => {
    const previewType = this.model.get('previewType');
    const owningEntityApiId = this.model.get('owningEntityApiId');
    const {url, previewFile, pdfPreviewFile} = this.model.blob();
    const chosenFile = previewType === 'image' ? previewFile : pdfPreviewFile;
    if (chosenFile == null) {
      throw new Error('previewFile is null');
    }

    renderToDOM(
      <ExternalFileOptions
        availablePreviewTypes={[
          ...(previewFile?.availablePreviewTypes ?? []),
          ...(pdfPreviewFile?.availablePreviewTypes ?? []),
        ]}
        previewType={previewType}
        updatePreviewType={this.updatePreviewType}
        printOptions={[]}
        generateDownloadUrl={async (isPreview: boolean) =>
          isPreview ? await performSignedFileQuery(chosenFile.id, chosenFile.url, owningEntityApiId) : url
        }
        isPreviewTransformed={false}
        closeTab={() => {
          this.model.close();
        }}
      />,
      this.el
    );
    return this;
  };

  remove(): Backbone.View<BlobLinkTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

BlobLinkTabView.prototype.className = 'external-file-wrapper buffer-tab buffer-tab--no-padding';

export {BlobLinkTabState as tabState, BlobLinkTabView as tabView, BlobLinkOptionsView as optionsView};
